import React from 'react'
import { GridColDef, GridRowModel, GridRenderCellParams } from '@mui/x-data-grid'
import { GridColumnType, GridCardHint, ExtendedGridColDef } from '@grid/types'
import dayjs from 'dayjs'

/** 그리드가 스스로 만드는 열 — 카드에는 없다 */
export const CARD_SKIP_FIELDS = ['idx', 'actions', '__check__', 'detail']

export interface CardSection {
	title: string
	fields: string[]
}

/** 컬럼 정의에서 유도한 카드 모델 (RESPONSIVE_PLAN §5.2) */
export interface CardModel {
	title: string
	status: string | null
	subtitle: string | null
	meta: string[]
	hide: string[]
	sections: CardSection[]
	/** 빠른 검색이 보는 열 */
	searchFields: string[]
	/** 정렬 메뉴에 나오는 열 */
	sortable: { field: string; headerName: string }[]
	byField: Record<string, ExtendedGridColDef>
}

export const resolveCardModel = (columns: GridColumnType): CardModel => {
	const cols = columns.columns.filter(c => !CARD_SKIP_FIELDS.includes(c.field))
	const hint: GridCardHint = columns.card ?? {}
	const byField: Record<string, ExtendedGridColDef> = {}
	cols.forEach(c => {
		byField[c.field] = c
	})
	const has = (f?: string | null): f is string => !!f && f in byField

	const title = has(hint.title) ? hint.title : cols[0]?.field ?? 'id'
	const statusCandidate = has(hint.status)
		? hint.status
		: has('status') && title !== 'status'
		? 'status'
		: null
	const subtitle = has(hint.subtitle) && hint.subtitle !== title ? hint.subtitle : null
	const hide = (hint.hide ?? []).filter(has)

	const taken = new Set<string>([title, ...(statusCandidate ? [statusCandidate] : [])])
	if (subtitle) taken.add(subtitle)

	const meta = (hint.meta ?? []).filter(f => has(f) && !taken.has(f)).slice(0, 4)
	if (meta.length === 0 && !hint.meta) {
		cols.forEach(c => {
			if (meta.length >= 4) return
			if (taken.has(c.field) || hide.includes(c.field)) return
			meta.push(c.field)
		})
	}
	meta.forEach(f => taken.add(f))

	// 자세히 섹션 — 힌트가 있으면 그것, 나머지(감사 열 제외)는 「기타」로. 없으면 「정보」 하나
	const sections: CardSection[] = []
	const covered = new Set<string>()
	if (hint.sections && hint.sections.length > 0) {
		hint.sections.forEach(s => {
			const fields = s.fields.filter(f => has(f) && !hide.includes(f))
			fields.forEach(f => covered.add(f))
			if (fields.length > 0) sections.push({ title: s.title, fields })
		})
		const rest = cols
			.map(c => c.field)
			.filter(f => !covered.has(f) && !hide.includes(f) && f !== title)
		if (rest.length > 0) sections.push({ title: '기타', fields: rest })
	} else {
		sections.push({
			title: '정보',
			fields: cols.map(c => c.field).filter(f => !hide.includes(f) && f !== title),
		})
	}

	const searchFields = Array.from(
		new Set<string>([
			title,
			...(subtitle ? [subtitle] : []),
			...(statusCandidate ? [statusCandidate] : []),
			...meta,
			...cols.filter(c => c.type === undefined || c.type === 'string').map(c => c.field),
		]),
	).filter(f => !hide.includes(f))

	const sortable = cols
		.filter(c => !hide.includes(c.field) && c.sortable !== false)
		.map(c => ({ field: c.field, headerName: String(c.headerName ?? c.field) }))

	return {
		title,
		status: statusCandidate,
		subtitle,
		meta,
		hide,
		sections,
		searchFields,
		sortable,
		byField,
	}
}

/** DataGrid 없이 열 정의의 valueGetter → valueFormatter 를 부르기 위한 최소 api */
export const makeApiStub = (rows: GridRowModel[]) => {
	const api: any = {
		getAllRowIds: () => rows.map(r => r.id),
		getRow: (id: any) => rows.find(r => r.id === id),
		getRowModels: () => new Map(rows.map(r => [r.id, r])),
		setEditCellValue: () => Promise.resolve(true),
	}
	return { current: api }
}

export const rawValue = (col: GridColDef, row: GridRowModel, apiRef: any): any => {
	const v = row[col.field]
	if (typeof col.valueGetter === 'function') {
		try {
			return (col.valueGetter as any)(v, row, col, apiRef)
		} catch {
			return v
		}
	}
	return v
}

const isDateLike = (v: any): boolean =>
	v instanceof Date ||
	(typeof v === 'string' &&
		/^\d{4}-\d{2}-\d{2}/.test(v) &&
		!Number.isNaN(new Date(v).getTime()))

/** 셀 표시값 — valueFormatter → 타입 기본 표기(날짜 yyyy-MM-dd · 불리언 예/아니오 · 숫자 천 단위) */
export const formattedValue = (
	col: GridColDef,
	row: GridRowModel,
	apiRef: any,
): string => {
	const v = rawValue(col, row, apiRef)
	if (typeof col.valueFormatter === 'function') {
		try {
			const f = (col.valueFormatter as any)(v, row, col, apiRef)
			if (f === null || f === undefined) return ''
			if (typeof f === 'boolean') return f ? '예' : '아니오'
			return String(f)
		} catch {
			/* fallthrough */
		}
	}
	if (v === null || v === undefined || v === '') return ''
	if (col.type === 'boolean' || typeof v === 'boolean') {
		return v === true || v === 1 || v === '1' ? '예' : '아니오'
	}
	if (col.type === 'singleSelect') {
		const opts: any = (col as any).valueOptions
		const list = typeof opts === 'function' ? opts({ row }) : opts
		if (Array.isArray(list)) {
			const hit = list.find((o: any) => (typeof o === 'object' ? o.value === v : o === v))
			if (hit !== undefined)
				return typeof hit === 'object' ? String(hit.label) : String(hit)
		}
	}
	if (col.type === 'date' || col.type === 'dateTime' || isDateLike(v)) {
		const d = dayjs(v)
		if (d.isValid()) {
			return col.type === 'dateTime' || (typeof v === 'string' && v.length > 10)
				? d.format('YYYY-MM-DD HH:mm')
				: d.format('YYYY-MM-DD')
		}
	}
	if (typeof v === 'number') return Number.isFinite(v) ? v.toLocaleString('ko-KR') : ''
	if (typeof v === 'object') {
		if (typeof v.name === 'string') return v.name
		return ''
	}
	return String(v)
}

/** 페이지가 넘긴 renderCell 을 카드에서 부르기 위한 params shim (RESPONSIVE_PLAN §5.2) */
export const renderCellShim = (
	col: GridColDef,
	row: GridRowModel,
	apiRef: any,
	renderCell?: (params: GridRenderCellParams) => JSX.Element,
): React.ReactNode => {
	const value = rawValue(col, row, apiRef)
	const formatted = formattedValue(col, row, apiRef)
	if (typeof renderCell === 'function') {
		const params: any = {
			id: row.id,
			row,
			field: col.field,
			value,
			formattedValue: formatted,
			colDef: col,
			api: apiRef.current,
			hasFocus: false,
			tabIndex: -1,
			cellMode: 'view',
			isEditable: false,
			rowNode: { id: row.id, type: 'leaf', depth: 0, parent: null, groupingKey: null },
		}
		try {
			return renderCell(params)
		} catch {
			return formatted
		}
	}
	return formatted
}

/** 정렬 비교 — 숫자·날짜·문자 */
export const compareValues = (a: any, b: any): number => {
	const na = a === null || a === undefined || a === ''
	const nb = b === null || b === undefined || b === ''
	if (na && nb) return 0
	if (na) return 1
	if (nb) return -1
	if (typeof a === 'number' && typeof b === 'number') return a - b
	if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b)
	if (isDateLike(a) && isDateLike(b)) {
		return new Date(a).getTime() - new Date(b).getTime()
	}
	return String(a).localeCompare(String(b), 'ko')
}

/** URL 조각 키 — 한 페이지에 그리드가 둘(박스)일 때 서로 안 섞이게. 한글 제목은 짧은 해시로 */
export const gridUrlKey = (title?: string): string => {
	const t = title ?? 'grid'
	const ascii = t
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-|-$/g, '')
	if (ascii.length >= 2) return ascii
	let h = 0
	for (let i = 0; i < t.length; i += 1) h = (h * 31 + t.charCodeAt(i)) >>> 0
	return `g${h.toString(36)}`
}

/** 값이 비었는가 — 카드 메타에서 빈 칸은 그리지 않는다 */
export const isEmptyValue = (v: any): boolean =>
	v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0)

/**
 * 상태 자리 — 페이지 renderCell 이 있으면 그것(칩), 불리언 열이면 참일 때만 헤더명 칩, 그 외 표시값.
 */
export const renderStatus = (
	col: GridColDef,
	row: GridRowModel,
	apiRef: any,
	renderCell?: (params: GridRenderCellParams) => JSX.Element,
): React.ReactNode => {
	if (typeof renderCell === 'function')
		return renderCellShim(col, row, apiRef, renderCell)
	const v = rawValue(col, row, apiRef)
	if (col.type === 'boolean' || typeof v === 'boolean') {
		const on = v === true || v === 1 || v === '1'
		if (!on) return null
		return React.createElement(
			'span',
			{ className: 'boChip outline' },
			String(col.headerName ?? col.field),
		)
	}
	const f = formattedValue(col, row, apiRef)
	return f === '' ? null : React.createElement('span', { className: 'boChip' }, f)
}
